import React, { useContext, useRef, useState } from 'react';
import {
    Container,
    Button,
    Typography,
    Box,
    InputAdornment, Divider
} from "@mui/material";
import Logo from "../Images/Logo.png";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import ValidatedTextField from "../Components/Inputs/ValidatedTextField";
import { alphaNumericCodeValidator } from "../Helpers/ValidationHelpers";
import { IsOnboardedContext } from "../Contexts/IsOnboardedContext";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useDispatch } from "react-redux";
import { postCustomer } from "../redux/CustomersSlice";
import TrimObjectStrings from "../Helpers/TrimObjectStrings";
import {Link} from "react-router-dom";
import InfoIcon from "@mui/icons-material/Info";
import LoadingWithStatusOverlay from "../Components/Loading/LoadingWithStatusOverlay";
import {useKindeAuth} from "@kinde-oss/kinde-auth-react";
import SubmitButton from "../Components/Inputs/SubmitButton";

function Onboarding() {
    const dispatch = useDispatch();
    const context = useContext(IsOnboardedContext);
    const [anchorElUser, setAnchorElUser] = useState(null);
    const formValid = useRef(false);
    const [activeForm, setActiveForm] = useState({
        customerCode: ""
    });

    const [loadingStatus, setLoadingStatus] = useState("idle");
    const [loadingMessage, setLoadingMessage] = useState("");

    const { logout } = useKindeAuth();
    const handleSubmit = (event) => {
        event.preventDefault();
        if (formValid.current) {
            setLoadingStatus("loading");
            setLoadingMessage("Setting things up...");

            dispatch(postCustomer(TrimObjectStrings({
                customerCode: activeForm.customerCode,
                adminUserEmail: context?.user?.email,
                tenantId: context?.orgDetails?.code,
                name: context?.orgDetails?.name,
                adminUserId: context?.user?.id
            }))).unwrap()
                .then(() => {
                    setLoadingStatus("success");
                    setLoadingMessage("Setup complete!");
                    setTimeout(() => {
                        handleToHome();
                    }, 1000);
                })
                .catch((error) => {
                    console.error('Failed to onboard customer', error);
                    setLoadingStatus("error");
                    setLoadingMessage("Something went wrong. Please try again.");
                })
        }
    };

    const handleToHome = () => {
        window.location.href = "/";
    }

    const handleOpenUserMenu = (event) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const handleLogout = () => {
        sessionStorage.setItem('hasLoggedOut', 'true');
        logout()
            .then(() => {
                handleCloseUserMenu();
            })
            .catch(error => {
                console.error('Logout failed:', error);
                handleCloseUserMenu();
            });
    }

    return (
        <>
            <LoadingWithStatusOverlay
                status={loadingStatus}
                message={loadingMessage}
                onComplete={() => {
                    setLoadingStatus("idle");
                    setLoadingMessage("");
                }}
            />
            <AppBar position="fixed">
                <Container maxWidth="xl">
                    <Toolbar disableGutters sx={{ justifyContent: 'center' }}>
                        <Box sx={{ display: 'flex', flexGrow: 1 }}>
                            <Box
                                component="img"
                                sx={{
                                    height: 50
                                }}
                                alt="Cybernation Industries logo"
                                src={Logo}
                            />
                        </Box>
                        <Box sx={{ flexGrow: 0 }}>
                            <Tooltip title="Open settings">
                                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                                    <Avatar alt="User avatar" src={context?.user?.profilePictureUrl} />
                                </IconButton>
                            </Tooltip>
                            <Menu
                                sx={{ mt: '45px' }}
                                id="menu-appbar"
                                anchorEl={anchorElUser}
                                anchorOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                keepMounted
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                open={Boolean(anchorElUser)}
                                onClose={handleCloseUserMenu}
                            >
                                <MenuItem onClick={() => handleLogout()}>
                                    <Typography textAlign="center">Logout</Typography>
                                </MenuItem>
                            </Menu>
                        </Box>
                    </Toolbar>
                </Container>
            </AppBar>
            {
                context?.onboardedStatus ?
                    <Container sx={{ marginY: 15 }}>
                        <Typography variant="h1">{`Thanks for signing up, ${context.userName}!`}</Typography>
                        <Typography variant="h6" sx={{ my: 1 }}>You've already successfully onboarded.</Typography>
                        <Box sx={{ position: 'relative', display: 'inline-flex', m: 0 }}>
                            <Link to={'/'}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    sx={{ mt: 1, mr: 1 }}
                                >
                                    Home
                                </Button>
                            </Link>
                        </Box>
                    </Container> :
                    <Container sx={{ marginY: 15 }}>
                        <Typography variant="h1">{!!context?.userName ? `Thanks for signing up, ${context?.userName}!` : `Thanks for signing up!`}</Typography>
                        <Typography variant="h3" sx={{ my: 1 }}>Just one more step.</Typography>
                        <Divider sx={{ my: 2}} />
                        <Box
                            component="form"
                            sx={{
                                maxWidth: 600,
                                mt: 2,
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                                '& .MuiTextField-root': { width: '40ch' }
                            }}
                            onSubmit={handleSubmit}
                        >
                            <Typography variant="h5">Create a Company Code</Typography>
                            <Typography>
                                This is a 3-6 letter abbreviation of your company name.
                            </Typography>
                            <Box
                            >
                                <ValidatedTextField
                                    id="customerCode"
                                    label="Company Code"
                                    validator={alphaNumericCodeValidator}
                                    onChange={isValid => (formValid.current = isValid)}
                                    setActiveForm={setActiveForm}
                                    initialValue={activeForm?.customerCode}
                                    fieldPath={['customerCode']}
                                    infoAdornment={<InputAdornment position="end">
                                        <Tooltip enterTouchDelay={200} disableFocusListener title={"e.g. 'ABC' for the Apples and Bananas Company."}>
                                            <InfoIcon />
                                        </Tooltip>
                                    </InputAdornment>}
                                    required
                                />
                            </Box>
                            <SubmitButton
                                message="Complete Setup"
                                disabled={!formValid.current}
                                sx={{ width: 170 }}
                            />
                        </Box>
                    </Container>
            }
        </>
    );
}

export default Onboarding;